import {
  GridColDef,
  GridColumnVisibilityModel,
  GridGroupNode,
  GridInputRowSelectionModel,
} from '@mui/x-data-grid';
import {
  GridCellParams,
  GridColumnOrderChangeParams,
  GridExperimentalProFeatures,
  GridRowHeightParams,
  GridRowHeightReturnValue,
  GridRowParams,
  GridDensity,
  GridRowIdGetter,
  GridSlotsComponentsProps,
  GridFeatureMode,
  GridFilterModel,
  GridCallbackDetails,
  GridInitialState,
  GridSortModel,
  GridRowClassNameParams,
  GridRowSelectionModel,
  GridValidRowModel,
  GridGroupingColDefOverride,
} from '@mui/x-data-grid-pro';
import { GridApiPro } from '@mui/x-data-grid-pro/models/gridApiPro';
import { JSX, ReactNode } from 'react';

export enum GridAllignmentTypes {
  Center = 'center',
  Left = 'left',
  Right = 'right',
}

export interface DataGridProps {
  columns: Array<GridColDef>;
  rows: Array<GridValidRowModel>;
  density?: GridDensity;
  getRowId?: GridRowIdGetter;
  componentsProps?: GridSlotsComponentsProps;
  columnVisibilityModel?: GridColumnVisibilityModel;
  disableColumnFilter?: boolean;
  disableColumnMenu?: boolean;
  disableColumnSelector?: boolean;
  disableDensitySelector?: boolean;
  checkboxSelection?: boolean;
  getDetailPanelHeight?: (params: GridRowParams) => number | 'auto';
  getDetailPanelContent?: (params: GridRowParams) => ReactNode;
  filterMode?: GridFeatureMode;
  onFilterModelChange?: (model: GridFilterModel, details?: GridCallbackDetails<'filter'>) => void;
  loading?: boolean;
  rowCount?: number;
  rowsPerPageOptions?: Array<number>;
  pagination?: boolean;
  page?: number;
  pageSize?: number;
  paginationMode?: GridFeatureMode;
  initialState?: GridInitialState;
  customToolbar?: () => JSX.Element;
  onPageChange?: (newPage: number) => void;
  onPageSizeChange?: (newPageSize: number) => void;
  filterModel?: GridFilterModel;
  sortingMode?: GridFeatureMode;
  onSortModelChange?: (model: GridSortModel, details: GridCallbackDetails) => void;
  getRowClassName?: (params: GridRowClassNameParams) => string;
  selectionModel?: GridInputRowSelectionModel;
  onSelectionModelChange?: (
    rowSelectionModel: GridRowSelectionModel,
    details: GridCallbackDetails
  ) => void;
  hideFooter?: boolean;
  autoHeight?: boolean;
  treeData?: boolean;
  /**
   * Left open deliberately. Callers pass a callback typed to their own row
   * (`(row: LinkedChildServiceData) => …`), and a parameter widened to
   * `GridValidRowModel` is contravariantly incompatible with every one of them.
   * Typing this properly means making the whole props interface generic in the
   * row type.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getTreeDataPath?: (row: any) => Array<string>;
  defaultGroupingExpansionDepth?: number;
  groupingColDef?: GridGroupingColDefOverride;
  getRowHeight?: (params: GridRowHeightParams) => GridRowHeightReturnValue;
  getEstimatedRowHeight?: (params: GridRowHeightParams) => number | null;
  isRowSelectable?: (params: GridRowParams) => boolean;
  onColumnOrderChange?: (params: GridColumnOrderChangeParams) => void;
  setColumns?: (params: GridColDef[]) => void;
  customFooter?: () => JSX.Element;
  onCellClick?: (params: GridCellParams) => void;
  onCellEditStop?: (params: GridCellParams) => void;
  apiRef?: React.MutableRefObject<GridApiPro>;
  experimentalFeatures?: Partial<GridExperimentalProFeatures>;
  getCellClassName?: (params: GridCellParams) => string;
  showCellVerticalBorder?: boolean;
  headerHeight?: number;
  onCellDoubleClick?: (params: GridCellParams) => void;
  isGroupExpandedByDefault?: (node: GridGroupNode) => boolean;
  serviceColumns?: Array<GridColDef>;
  disableSelectionOnClick?: boolean;
  hasFilterPanelWithSearch?: boolean;
  filtersSearch?: () => void;
  hideFooterComponent?: boolean;
  defaultReport?: string;
  id?: string;
  pageName?: string;
  rowHeight?: number;
  // Virtualization is disabled by default across the app; opt back in when row counts can be large.
  disableVirtualization?: boolean;
  /**
   * Skins the grid to match the v4 tables: 11px uppercase headers, 12px cells,
   * hairline row rules and no outer border, so it sits inside a `Panel` as one
   * surface rather than a card within a card.
   *
   * Opt-in — the grids on legacy screens keep the look they have.
   */
  v4Skin?: boolean;
}
